/**
 * FIELD VALIDATION & MALICIOUS CONTENT CONSTANTS
 *
 * Central source of truth for all security rules including dangerous characters,
 * patterns, and field-specific validation configurations.
 */

import {
  DESCRIPTION_CHAR_LIMIT,
  EMAIL_CHAR_LIMIT,
  NAME_CHAR_LIMIT,
  PASSWORD_CHAR_LIMIT,
  TEXT_CHAR_LIMIT,
  TOURNAMENT_PASSWORD_CHAR_LIMIT,
  USERNAME_CHAR_LIMIT,
} from './fieldLimits.constants';

// DANGEROUS CHARACTERS - Characters that can be used for XSS/injection attacks
export const DANGEROUS_CHARACTERS = {
  // HTML/XML control characters
  ANGLE_BRACKETS: ['<', '>'],
  AMPERSAND: ['&'],
  QUOTES: ['"', "'", '`'],

  // Script execution characters
  PARENTHESES: ['(', ')'],
  BRACES: ['{', '}'],
  BRACKETS: ['[', ']'],

  // Code/protocol injection
  SEMICOLON: [';'],
  COLON: [':'],
  EQUALS: ['='],
  PIPE: ['|'],
  BACKSLASH: ['\\'],
  FORWARD_SLASH: ['/'],

  // URL/encoding related
  PERCENT: ['%'],
  HASH: ['#'],
  PLUS: ['+'],

  // Advanced injection vectors
  DOLLAR: ['$'], // Template literals, variable substitution
  TILDE: ['~'], // Bitwise operations
  CARET: ['^'], // XOR operations
  ASTERISK: ['*'], // Wildcard operations

  // Control/formatting that can hide malicious content
  NULL_BYTE: ['\0'],
  TAB: ['\t'],
  NEWLINE: ['\n', '\r'],
} as const;

// CHARACTER SETS - Organized by threat level
export const CHARACTER_SETS = {
  // Always dangerous - block everywhere
  DANGEROUS: [
    ...DANGEROUS_CHARACTERS.AMPERSAND,
    ...DANGEROUS_CHARACTERS.QUOTES,
    ...DANGEROUS_CHARACTERS.NULL_BYTE,
    ...DANGEROUS_CHARACTERS.FORWARD_SLASH, // Path traversal
    ...DANGEROUS_CHARACTERS.BACKSLASH, // Path traversal
    ...DANGEROUS_CHARACTERS.ANGLE_BRACKETS, // XSS injection
    ...DANGEROUS_CHARACTERS.PARENTHESES, // Script injection
    ...DANGEROUS_CHARACTERS.BRACES, // Script injection
    ...DANGEROUS_CHARACTERS.BRACKETS, // Script injection
    ...DANGEROUS_CHARACTERS.SEMICOLON, // Script injection
    ...DANGEROUS_CHARACTERS.EQUALS, // Script injection
    ...DANGEROUS_CHARACTERS.DOLLAR, // Script injection
    ...DANGEROUS_CHARACTERS.PIPE, // Command injection
    ...DANGEROUS_CHARACTERS.COLON, // Protocol injection
    ...DANGEROUS_CHARACTERS.PERCENT, // URL encoding
    ...DANGEROUS_CHARACTERS.HASH, // URL fragments
    ...DANGEROUS_CHARACTERS.PLUS, // URL encoding
  ],

  // Advanced attacks - for MAXIMUM security
  ADVANCED_INJECTION: [
    ...DANGEROUS_CHARACTERS.TILDE,
    ...DANGEROUS_CHARACTERS.CARET,
    ...DANGEROUS_CHARACTERS.ASTERISK,
  ],

  // Safe for all text (letters, numbers, basic punctuation)
  SAFE_TEXT: [
    'a-zA-Z0-9', // Letters and numbers
    ' ', // Spaces
    '.', // Period
    ',', // Comma
    '!', // Exclamation
    '?', // Question mark
    '-', // Hyphen
    '_', // Underscore
  ],

  // Safe for usernames
  SAFE_USERNAME: [
    'a-zA-Z0-9', // Letters and numbers
    '_', // Underscore
    '-', // Hyphen
    '.', // Period
  ],

  // Safe for emails
  SAFE_EMAIL: [
    'a-z0-9', // Letters and numbers (lowercase)
    '@', // At symbol
    '.', // Period
    '_', // Underscore
    '+', // Plus (for email aliases)
    '-', // Hyphen
  ],
} as const;

// SECURITY LEVELS - Clear hierarchy
export const SECURITY_LEVELS = {
  // Maximum security - everything dangerous + advanced (USED EVERYWHERE)
  MAXIMUM: [...CHARACTER_SETS.DANGEROUS, ...CHARACTER_SETS.ADVANCED_INJECTION],

  // High security - all dangerous characters
  HIGH: [...CHARACTER_SETS.DANGEROUS],

  // Basic security - only for very permissive fields (if needed)
  BASIC: [
    ...DANGEROUS_CHARACTERS.AMPERSAND,
    ...DANGEROUS_CHARACTERS.QUOTES,
    ...DANGEROUS_CHARACTERS.NULL_BYTE,
    ...DANGEROUS_CHARACTERS.FORWARD_SLASH,
    ...DANGEROUS_CHARACTERS.BACKSLASH,
  ],
} as const;

// MALICIOUS PATTERNS - Organized by attack type
export const MALICIOUS_PATTERNS = {
  // Script tags
  SCRIPT_TAGS: [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /<\/?\s*script\b[^>]*>/gi,
  ],

  // Protocol injection
  JAVASCRIPT_PROTOCOL: [
    /javascript\s*:/gi,
    /vbscript\s*:/gi,
    /data\s*:/gi,
    /livescript\s*:/gi,
  ],

  // Event handlers
  EVENT_HANDLERS: [
    /on\w+\s*=/gi, // onclick=, onload=, etc.
    /on[a-z]+\s*=\s*['"]/gi, // More specific event handlers
  ],

  // HTML injection
  HTML_TAGS: [
    /<iframe\b[^>]*>/gi,
    /<object\b[^>]*>/gi,
    /<embed\b[^>]*>/gi,
    /<form\b[^>]*>/gi,
    /<input\b[^>]*>/gi,
    /<link\b[^>]*>/gi,
    /<meta\b[^>]*>/gi,
  ],

  // Encoded attacks
  ENCODED_ATTACKS: [
    /&#x?[0-9a-f]+;/gi, // Numeric HTML entities
    /%[0-9a-f]{2}/gi, // URL encoding
    /\\u[0-9a-f]{4}/gi, // Unicode escapes
    /\\x[0-9a-f]{2}/gi, // Hex escapes
  ],

  // CSS injection
  CSS_INJECTION: [
    /expression\s*\(/gi,
    /behavior\s*:/gi,
    /@import/gi,
    /javascript\s*:/gi,
  ],

  // SQL injection (just in case)
  SQL_INJECTION: [
    /union\s+select/gi,
    /drop\s+table/gi,
    /insert\s+into/gi,
    /delete\s+from/gi,
    /update\s+set/gi,
  ],
} as const;

// FIELD SECURITY CONFIGS - MAXIMUM security everywhere with ALL patterns
export const FIELD_SECURITY_CONFIGS = {
  // Maximum security for critical fields
  PASSWORD: {
    blockedCharacters: SECURITY_LEVELS.MAXIMUM,
    patterns: [
      ...MALICIOUS_PATTERNS.SCRIPT_TAGS,
      ...MALICIOUS_PATTERNS.JAVASCRIPT_PROTOCOL,
      ...MALICIOUS_PATTERNS.EVENT_HANDLERS,
      ...MALICIOUS_PATTERNS.HTML_TAGS,
      ...MALICIOUS_PATTERNS.ENCODED_ATTACKS,
      ...MALICIOUS_PATTERNS.CSS_INJECTION,
      ...MALICIOUS_PATTERNS.SQL_INJECTION,
    ] as RegExp[],
    maxLength: PASSWORD_CHAR_LIMIT,
  },

  // Maximum security for user-facing text
  NAME: {
    blockedCharacters: SECURITY_LEVELS.MAXIMUM,
    patterns: [
      ...MALICIOUS_PATTERNS.SCRIPT_TAGS,
      ...MALICIOUS_PATTERNS.JAVASCRIPT_PROTOCOL,
      ...MALICIOUS_PATTERNS.EVENT_HANDLERS,
      ...MALICIOUS_PATTERNS.HTML_TAGS,
      ...MALICIOUS_PATTERNS.ENCODED_ATTACKS,
      ...MALICIOUS_PATTERNS.CSS_INJECTION,
      ...MALICIOUS_PATTERNS.SQL_INJECTION,
    ] as RegExp[],
    maxLength: NAME_CHAR_LIMIT,
  },

  // Maximum security for usernames
  USERNAME: {
    blockedCharacters: SECURITY_LEVELS.MAXIMUM,
    patterns: [
      ...MALICIOUS_PATTERNS.SCRIPT_TAGS,
      ...MALICIOUS_PATTERNS.JAVASCRIPT_PROTOCOL,
      ...MALICIOUS_PATTERNS.EVENT_HANDLERS,
      ...MALICIOUS_PATTERNS.HTML_TAGS,
      ...MALICIOUS_PATTERNS.ENCODED_ATTACKS,
      ...MALICIOUS_PATTERNS.CSS_INJECTION,
      ...MALICIOUS_PATTERNS.SQL_INJECTION,
    ] as RegExp[],
    allowedCharacters: CHARACTER_SETS.SAFE_USERNAME,
    maxLength: USERNAME_CHAR_LIMIT,
  },

  // Maximum security for emails
  EMAIL: {
    blockedCharacters: SECURITY_LEVELS.MAXIMUM,
    patterns: [
      ...MALICIOUS_PATTERNS.SCRIPT_TAGS,
      ...MALICIOUS_PATTERNS.JAVASCRIPT_PROTOCOL,
      ...MALICIOUS_PATTERNS.EVENT_HANDLERS,
      ...MALICIOUS_PATTERNS.HTML_TAGS,
      ...MALICIOUS_PATTERNS.ENCODED_ATTACKS,
      ...MALICIOUS_PATTERNS.CSS_INJECTION,
      ...MALICIOUS_PATTERNS.SQL_INJECTION,
    ] as RegExp[],
    allowedCharacters: CHARACTER_SETS.SAFE_EMAIL,
    maxLength: EMAIL_CHAR_LIMIT,
  },

  // Maximum security for descriptions
  DESCRIPTION: {
    blockedCharacters: SECURITY_LEVELS.MAXIMUM,
    patterns: [
      ...MALICIOUS_PATTERNS.SCRIPT_TAGS,
      ...MALICIOUS_PATTERNS.JAVASCRIPT_PROTOCOL,
      ...MALICIOUS_PATTERNS.EVENT_HANDLERS,
      ...MALICIOUS_PATTERNS.HTML_TAGS,
      ...MALICIOUS_PATTERNS.ENCODED_ATTACKS,
      ...MALICIOUS_PATTERNS.CSS_INJECTION,
      ...MALICIOUS_PATTERNS.SQL_INJECTION,
    ] as RegExp[],
    maxLength: DESCRIPTION_CHAR_LIMIT,
  },

  // Maximum security for general text
  TEXT: {
    blockedCharacters: SECURITY_LEVELS.MAXIMUM,
    patterns: [
      ...MALICIOUS_PATTERNS.SCRIPT_TAGS,
      ...MALICIOUS_PATTERNS.JAVASCRIPT_PROTOCOL,
      ...MALICIOUS_PATTERNS.EVENT_HANDLERS,
      ...MALICIOUS_PATTERNS.HTML_TAGS,
      ...MALICIOUS_PATTERNS.ENCODED_ATTACKS,
      ...MALICIOUS_PATTERNS.CSS_INJECTION,
      ...MALICIOUS_PATTERNS.SQL_INJECTION,
    ] as RegExp[],
    maxLength: TEXT_CHAR_LIMIT,
  },

  // Maximum security for tournament passwords
  TOURNAMENT_PASSWORD: {
    blockedCharacters: SECURITY_LEVELS.MAXIMUM,
    patterns: [
      ...MALICIOUS_PATTERNS.SCRIPT_TAGS,
      ...MALICIOUS_PATTERNS.JAVASCRIPT_PROTOCOL,
      ...MALICIOUS_PATTERNS.EVENT_HANDLERS,
      ...MALICIOUS_PATTERNS.HTML_TAGS,
      ...MALICIOUS_PATTERNS.ENCODED_ATTACKS,
      ...MALICIOUS_PATTERNS.CSS_INJECTION,
      ...MALICIOUS_PATTERNS.SQL_INJECTION,
    ] as RegExp[],
    maxLength: TOURNAMENT_PASSWORD_CHAR_LIMIT,
  },
} as const;

// UTILITY FUNCTIONS for field validation
export const createCharacterRegex = (characters: readonly string[]): RegExp => {
  // Escape special regex characters
  const escaped = characters.map((char) =>
    char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  );
  return new RegExp(`[${escaped.join('')}]`, 'g');
};

export const createBlockedCharacterRegex = (
  securityLevel: readonly string[],
): RegExp => {
  return createCharacterRegex(securityLevel);
};

export const getAllPatterns = (
  patternGroups: readonly RegExp[][],
): RegExp[] => {
  return patternGroups.flat();
};
